import { type PermissionKey, RESERVED_EVENT_SLUGS } from "@vaulted/shared";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useParams } from "react-router";
import { Shell } from "./components/Shell";
import { Loading } from "./components/ui";
import { AccountPage } from "./pages/Account";
import { AuditPage } from "./pages/Audit";
import { BookPage } from "./pages/Book";
import { EventBookingPage } from "./pages/EventBooking";
import { AvailabilityPage } from "./pages/Availability";
import { CalendarPage } from "./pages/Calendar";
import { CheckInPage, EventPage, EventsPage } from "./pages/Events";
import { MobileRequestPage, MobileRequestsPage } from "./pages/MobileRequests";
import { CommunicationPage } from "./pages/Communication";
import { CatalogPage } from "./pages/Catalog";
import { ClientProfilePage } from "./pages/ClientProfile";
import { ClientsPage } from "./pages/Clients";
import { CatalogItemPage } from "./pages/CatalogItem";
import { PrintMenuPage } from "./pages/PrintMenu";
import { PlanPage } from "./pages/Plans";
import { ChartPage } from "./pages/Chart";
import { PendingChartsPage } from "./pages/PendingCharts";
import { CheckoutPage } from "./pages/Checkout";
import { FormsPage, KioskPage } from "./pages/Forms";
import { ConfirmPage } from "./pages/Confirm";
import { NoteLinkPage } from "./pages/NoteLink";
import { InventoryPage } from "./pages/Inventory";
import { PayPage } from "./pages/Pay";
import { LeavePage } from "./pages/Leave";
import { JoinPage } from "./pages/Join";
import { ReportsPage } from "./pages/Reports";
import { PayoutsPage } from "./pages/Payouts";
import { PastVisitImportPage } from "./pages/PastVisitImport";
import { ImportedRecordPage } from "./pages/ImportedRecord";
import { VisitPage } from "./pages/Visit";
import { DevicePage } from "./pages/Device";
import { FrontDeskPage } from "./pages/FrontDesk";
import { TodayPage } from "./pages/Today";
import { SettingsLayout } from "./components/SettingsLayout";
import { RolesPage } from "./pages/Roles";
import { SettingsPage } from "./pages/Settings";
import { SignIn } from "./pages/SignIn";
import { StaffPage } from "./pages/Staff";
import { StaffDetailPage } from "./pages/StaffDetail";
import { SessionProvider, useSession } from "./session";
import "@fontsource/manrope/400.css";
import "@fontsource/manrope/500.css";
import "@fontsource/manrope/600.css";
import "@fontsource/manrope/700.css";
import "@fontsource/marcellus/400.css";
import "./styles.css";

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } },
});

function Guard({ needs, children }: { needs: PermissionKey | PermissionKey[]; children: ReactNode }) {
  const { can } = useSession();
  if (!can(...(Array.isArray(needs) ? needs : [needs]))) {
    return (
      <div className="py-10">
        <h1 className="page-title">No access</h1>
        <p className="mt-2 text-ink-soft">Your role doesn't include this page. Ask an admin if you need it.</p>
        <Link to="/" className="mt-4 inline-block font-semibold text-brand">
          Go to home
        </Link>
      </div>
    );
  }
  return children;
}

function App() {
  const { me, loading } = useSession();
  if (loading) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Loading />
      </div>
    );
  }
  if (!me) return <SignIn />;
  return (
    <Routes>
      {/* Check-in iPad: no navigation, only this client's forms. */}
      <Route path="kiosk/:id" element={<Guard needs="appointments.manage"><KioskPage /></Guard>} />
      <Route element={<Shell />}>
        <Route index element={<TodayPage />} />
        <Route path="calendar" element={<Guard needs="appointments.manage"><CalendarPage /></Guard>} />
        <Route path="front-desk" element={<Guard needs={["appointments.manage", "mobile.confirm"]}><FrontDeskPage /></Guard>} />
        <Route path="communication" element={<Guard needs={["calls.view"]}><CommunicationPage /></Guard>} />
        <Route path="communication/:tab" element={<Guard needs={["calls.view"]}><CommunicationPage /></Guard>} />
        <Route path="voicemail" element={<Navigate to="/communication/phone" replace />} />
        <Route path="mobile-requests" element={<Navigate to="/front-desk" replace />} />
        <Route path="mobile-requests/tab/:tab" element={<Guard needs={["mobile.confirm", "appointments.manage"]}><MobileRequestsPage /></Guard>} />
        <Route path="mobile-requests/:id" element={<Guard needs={["mobile.confirm", "appointments.manage"]}><MobileRequestPage /></Guard>} />
        <Route path="events" element={<Guard needs={["events.manage"]}><EventsPage /></Guard>} />
        <Route path="events/:id" element={<Guard needs={["events.manage"]}><EventPage /></Guard>} />
        <Route path="events/check-in/:occurrenceId" element={<Guard needs={["events.manage", "appointments.manage"]}><CheckInPage /></Guard>} />
        <Route path="clients" element={<Guard needs={["clients.view_all", "clients.view_assigned"]}><ClientsPage /></Guard>} />
        <Route path="reports/:tab?" element={<Guard needs="reports.financial"><ReportsPage /></Guard>} />
        <Route path="payouts/:tab?" element={<Guard needs={["payouts.view_own", "reports.financial", "payouts.manage"]}><PayoutsPage /></Guard>} />
        <Route path="payments" element={<Navigate to="/reports/payments" replace />} />
        <Route path="checkout/:id" element={<Guard needs="appointments.manage"><CheckoutPage /></Guard>} />
        <Route path="pending-charts" element={<Guard needs={["charts.write", "charts.sign", "charts.view_all"]}><PendingChartsPage /></Guard>} />
        <Route path="pr-charts" element={<Navigate to="/pending-charts" replace />} />
        <Route path="visits/:id" element={<Guard needs={["appointments.manage", "charts.write"]}><VisitPage /></Guard>} />
        <Route path="charts/:id" element={<Guard needs={["clients.view_all", "clients.view_assigned"]}><ChartPage /></Guard>} />
        <Route path="clients/import/visits" element={<Guard needs="settings.manage"><PastVisitImportPage /></Guard>} />
        <Route path="records/:id" element={<ImportedRecordPage />} />
        <Route path="clients/:id" element={<Guard needs={["clients.view_all", "clients.view_assigned"]}><ClientProfilePage /></Guard>} />
        <Route path="inventory/:tab?" element={<Guard needs="inventory.view"><InventoryPage /></Guard>} />
        <Route path="availability" element={<Guard needs={["availability.edit_own", "availability.view_team", "availability.manage"]}><AvailabilityPage /></Guard>} />
        <Route path="availability/*" element={<Navigate to="/availability" replace />} />
        {/* Settings: a grouped list beside each page. */}
        <Route element={<SettingsLayout />}>
          <Route path="staff" element={<Guard needs="staff.manage"><StaffPage /></Guard>} />
          <Route path="staff/:id" element={<Guard needs="staff.manage"><StaffDetailPage /></Guard>} />
          <Route path="roles" element={<Guard needs="roles.manage"><RolesPage /></Guard>} />
          <Route path="catalog/print" element={<PrintMenuPage />} />
          <Route path="catalog/:tab?" element={<CatalogPage />} />
          <Route path="catalog/item/:id" element={<CatalogItemPage />} />
          <Route path="catalog/plan/:id" element={<PlanPage />} />
          <Route path="catalog/plan/new/:kind" element={<Guard needs="catalog.manage"><PlanPage /></Guard>} />
          <Route path="catalog/new/:type" element={<Guard needs="catalog.manage"><CatalogItemPage /></Guard>} />
          <Route path="audit" element={<Guard needs="audit.view"><AuditPage /></Guard>} />
          <Route path="settings/:tab?" element={<Guard needs="settings.manage"><SettingsPage /></Guard>} />
        </Route>
        <Route path="account" element={<AccountPage />} />
        <Route path="*" element={<p className="py-10 text-ink-soft">That page doesn't exist.</p>} />
      </Route>
    </Routes>
  );
}

/** Event links used to be /book/<clinic>/e/<event>. */
function OldEventLink() {
  const { event = "" } = useParams();
  return <Navigate to={`/${event}`} replace />;
}

/**
 * A one-part path that isn't one of the app's own pages is an event page. Decided here, under the
 * catch-all route, so the staff app's own routes still see the whole path (a "/:eventSlug" route
 * would hand them an empty one, and every page would be Today).
 */
function EventOrApp() {
  const { pathname } = useLocation();
  const one = pathname.match(/^\/([a-z0-9-]{3,40})\/?$/i)?.[1]?.toLowerCase();
  return one && !RESERVED_EVENT_SLUGS.includes(one) ? (
    <EventBookingPage slug={one} />
  ) : (
    <SessionProvider>
      <App />
    </SessionProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Client-facing booking: no staff sign-in. */}
          <Route path="/book/:slug" element={<BookPage />} />
          <Route path="/book/:slug/mobile" element={<BookPage />} />
          <Route path="/book/:slug/e/:event" element={<OldEventLink />} />
          <Route path="/forms/:token" element={<FormsPage />} />
          <Route path="/confirm/:token" element={<ConfirmPage />} />
          <Route path="/note/:token" element={<NoteLinkPage />} />
          <Route path="/device/:slug" element={<DevicePage />} />
          <Route path="/pay/:token" element={<PayPage />} />
          <Route path="/leave/:token" element={<LeavePage />} />
          <Route path="/join/:slug/:planId" element={<JoinPage />} />
          {/* Everything else: the staff app, or an event page at /<event-slug>. */}
          <Route path="*" element={<EventOrApp />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
